import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfirmDialogComponent, ConfirmDialogData } from '@shure/shared/angular/ui/components/dialogs/confirm';
import { InfoDialogComponent, InfoDialogData } from '@shure/shared/angular/ui/components/dialogs/info';

import { IgnoreDialogService } from './ignore-dialog.service';

export interface DialogLocalStorage {
	doNotShowAgain: string[];
}

@Injectable({ providedIn: 'root' })
export class CommonDialogsService {
	private readonly defaultDialogConfig: MatDialogConfig = {
		width: '400px'
	};

	constructor(
		private readonly dialog: MatDialog,
		private readonly ignoreDialogService: IgnoreDialogService
	) {}

	/**
	 * Open confirm dialog
	 */
	public openConfirmDialog(
		data: ConfirmDialogData,
		config: MatDialogConfig = this.defaultDialogConfig
	): Observable<boolean> {
		const dialogRef = this.openConfirmDialogWithReference(data, config);
		if (!dialogRef) {
			return of(true);
		}

		return dialogRef.afterClosed().pipe(
			map((result) => {
				if (!result || !result.confirmed) {
					return false;
				}

				this.ignoreDialogService.setIgnoredDialog(result.doNotShowDialog, data.doNotShowDialogId);

				return result.confirmed || false;
			})
		);
	}

	public openConfirmDialogWithReference(
		data: ConfirmDialogData,
		config: MatDialogConfig = this.defaultDialogConfig
	): MatDialogRef<ConfirmDialogComponent, { confirmed: boolean; doNotShowDialog: boolean }> | null {
		if (data.doNotShowDialogId && this.ignoreDialogService.isDialogIgnored(data.doNotShowDialogId)) {
			return null;
		}

		return this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
			...config,
			data: {
				title: data.title,
				body: typeof data.body === 'string' ? [data.body] : data.body,
				confirmLabel: data.confirmLabel,
				cancelLabel: data.cancelLabel,
				showIcon: data.showIcon,
				doNotShowDialogId: data.doNotShowDialogId,
				blockElectronMenuAccess: true
			}
		});
	}

	/**
	 * Open a info dialog
	 */
	public openInfoDialog(
		data: InfoDialogData,
		config: MatDialogConfig = this.defaultDialogConfig
	): MatDialogRef<InfoDialogComponent, boolean> {
		const dialogRef = this.dialog.open(InfoDialogComponent, {
			...this.defaultDialogConfig,
			...config,
			data: {
				title: data.title,
				body: typeof data.body === 'string' ? [data.body] : data.body,
				okLabel: data.okLabel,
				showWarningIcon: data.showWarningIcon,
				showBetaIcon: data.showBetaIcon,
				doNotShowDialogId: data.doNotShowDialogId
			}
		});

		dialogRef.afterClosed().subscribe((doNotShowDialog) => {
			if (doNotShowDialog) {
				this.ignoreDialogService.setIgnoredDialog(doNotShowDialog, data.doNotShowDialogId);
			}
		});

		return dialogRef;
	}
}
